import React from "react";
import { MainLayout } from "@/components/layout/MainLayout";
import { Helmet } from "react-helmet";
import { ArrowRight, ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useTranslation } from "@/hooks/useTranslation";

const CookiePolicy = () => {
  const navigate = useNavigate();
  const { dir, isRTL } = useTranslation();

  return (
    <MainLayout>
      <Helmet>
        <title>Cookies Policy | My EyeControl</title>
        <meta name="description" content="Cookies Policy for the EyeControl Platform" />
      </Helmet>

      <div className="flex-1 flex flex-col bg-background p-4 overflow-hidden" dir={dir}>
        <div className="flex items-center gap-4 mb-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/legal")}
            className="hover:bg-primary/10"
          >
            {isRTL ? <ArrowRight className="w-6 h-6" /> : <ArrowLeft className="w-6 h-6" />}
          </Button>
          <h1 className="text-2xl font-bold text-foreground">Cookies Policy</h1>
        </div>

        <ScrollArea className="flex-1 pr-4">
          <div className="prose prose-sm max-w-none text-foreground/90 space-y-6 pb-8">
            <p className="text-muted-foreground">Last updated: December 31, 2024</p>

            <section>
              <p>When you visit or access our marketing website available at: www.eyecontrol.co.il (the "Marketing Website"), your personal account available at: my.eyecontrol.co.il (the "Home Platform"), or any other EyeControl website operates under the domains of eyecontrol.co.il or eyecontrol.health (the "MED/PRO Platforms")(hereinafter together: the "Websites" or "Platforms") we and our authorized third-party providers use cookies and other tracking technologies to collect and process data about your use of our Website and Platforms.</p>
              <p>Cookies allow us to automatically collect information about you and your online behaviour, as well as your device (for example your computer or mobile device), in order to improve your browsing experience and to enhance your navigation on our Website, Platforms, improve our services' performance, customize your experience on our Websites, Platforms and services, and for technical reasons. We also use this information to collect statistics about the usage of our services, perform analytics, deliver content which is tailored to your interests and administer services to our users, advertisers, publishers, customers and partners.</p>
              <p>We also allow third-party providers to collect information about you through cookies and other tracking technologies for the abovementioned purposes.</p>
              <p>If you have questions or concerns regarding this Cookies Policy please contact us at: <a href="mailto:hello@eyecontrol.co.il" className="text-primary hover:underline">hello@eyecontrol.co.il</a></p>
            </section>

            <section>
              <h2 className="text-xl font-semibold">What are cookies?</h2>
              <p>Cookies are small text files that a web server places on your computer or mobile device when you visit a webpage. When used, cookies can help make our Websites, Platforms and/or services more user-friendly, for example by remembering your language preferences and settings. You can find more information about cookies at <a href="https://www.allaboutcookies.org/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">https://www.allaboutcookies.org/</a>.</p>
            </section>

            <section>
              <h2 className="text-xl font-semibold">Storing cookies</h2>
              <p>We store cookies when you visit or access our Websites and Platforms – these are called "First Party Cookies". In addition, cookies are stored by other third parties (for example our analytics service providers, business partners and advertisers) – these are called "Third Party Cookies".</p>
              <p>Both types of cookies may be stored either for the duration of your visit on our Websites and/or Platforms, or for repeat visits.</p>
            </section>

            <section>
              <h2 className="text-xl font-semibold">What types of cookies do we use?</h2>
              <p>When you use our Websites or Platforms, we and our third-party providers may place a number of cookies on your device. These cookies include:</p>

              <h3 className="text-lg font-medium mt-4">Essential Cookies</h3>
              <p>These cookies are necessary for the Websites and Platforms to function. We use essential cookies to authenticate users, prevent fraudulent use of our Websites and/or Platforms, and allow their features to function properly. The Websites and Platforms cannot function properly without these cookies.</p>
              
              <div className="overflow-x-auto my-4">
                <table className="min-w-full border border-border">
                  <thead className="bg-muted">
                    <tr>
                      <th className="px-4 py-2 text-left border-b">Cookie Name</th>
                      <th className="px-4 py-2 text-left border-b">Provider/domain</th>
                      <th className="px-4 py-2 text-left border-b">Purpose</th>
                      <th className="px-4 py-2 text-left border-b">Duration</th>
                      <th className="px-4 py-2 text-left border-b">Type</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="px-4 py-2 border-b">Sid-prod</td>
                      <td className="px-4 py-2 border-b">my.eyecontrol.co.il</td>
                      <td className="px-4 py-2 border-b">Session ID for user login management</td>
                      <td className="px-4 py-2 border-b">Session</td>
                      <td className="px-4 py-2 border-b">1st Party</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-2 border-b">_csrf</td>
                      <td className="px-4 py-2 border-b">my.eyecontrol.co.il</td>
                      <td className="px-4 py-2 border-b">To validate the authenticity of requests made by users</td>
                      <td className="px-4 py-2 border-b">Session</td>
                      <td className="px-4 py-2 border-b">1st Party</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-2 border-b">XSRF-TOKEN</td>
                      <td className="px-4 py-2 border-b">www.eyecontrol.co.il</td>
                      <td className="px-4 py-2 border-b">Cookie for fraud detection of calls</td>
                      <td className="px-4 py-2 border-b">Session</td>
                      <td className="px-4 py-2 border-b">1st Party</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-2 border-b">hs</td>
                      <td className="px-4 py-2 border-b">www.eyecontrol.co.il</td>
                      <td className="px-4 py-2 border-b">Security Cookie for Hive (legacy)</td>
                      <td className="px-4 py-2 border-b">Session</td>
                      <td className="px-4 py-2 border-b">1st Party</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-2 border-b">sam</td>
                      <td className="px-4 py-2 border-b">www.eyecontrol.co.il</td>
                      <td className="px-4 py-2 border-b">Session cookie for identification</td>
                      <td className="px-4 py-2 border-b">6 months</td>
                      <td className="px-4 py-2 border-b">1st Party</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-2 border-b">SSR-caching</td>
                      <td className="px-4 py-2 border-b">www.eyecontrol.co.il</td>
                      <td className="px-4 py-2 border-b">Performance cookie for rendering</td>
                      <td className="px-4 py-2 border-b">24 hours</td>
                      <td className="px-4 py-2 border-b">1st Party</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-2 border-b">TS*</td>
                      <td className="px-4 py-2 border-b">www.eyecontrol.co.il</td>
                      <td className="px-4 py-2 border-b">Cookies for attack detection</td>
                      <td className="px-4 py-2 border-b">Session</td>
                      <td className="px-4 py-2 border-b">1st Party</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-2 border-b">bSession</td>
                      <td className="px-4 py-2 border-b">www.eyecontrol.co.il</td>
                      <td className="px-4 py-2 border-b">Used for system effectiveness measurement</td>
                      <td className="px-4 py-2 border-b">24 hours</td>
                      <td className="px-4 py-2 border-b">1st Party</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-2 border-b">server-session-bind</td>
                      <td className="px-4 py-2 border-b">www.eyecontrol.co.il</td>
                      <td className="px-4 py-2 border-b">Cookie for API protection</td>
                      <td className="px-4 py-2 border-b">Session</td>
                      <td className="px-4 py-2 border-b">1st Party</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-2 border-b">client-session-bind</td>
                      <td className="px-4 py-2 border-b">www.eyecontrol.co.il</td>
                      <td className="px-4 py-2 border-b">Cookie for API protection</td>
                      <td className="px-4 py-2 border-b">Session</td>
                      <td className="px-4 py-2 border-b">1st Party</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h3 className="text-lg font-medium mt-4">Functional Cookies</h3>
              <p>We use functional cookies to provide enhanced functionality and personalization, to remember your preferences, to diagnose server and software errors, and in cases of abuse, to track and mitigate the abuse. If you do not allow these cookies, some or all of the Websites or Platforms' services may not function properly.</p>
              
              <div className="overflow-x-auto my-4">
                <table className="min-w-full border border-border">
                  <thead className="bg-muted">
                    <tr>
                      <th className="px-4 py-2 text-left border-b">Cookie Name</th>
                      <th className="px-4 py-2 text-left border-b">Provider/domain</th>
                      <th className="px-4 py-2 text-left border-b">Purpose</th>
                      <th className="px-4 py-2 text-left border-b">Duration</th>
                      <th className="px-4 py-2 text-left border-b">Type</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="px-4 py-2 border-b">Zoho</td>
                      <td className="px-4 py-2 border-b">Zoho</td>
                      <td className="px-4 py-2 border-b">We use Zoho to provide support services to our Users. Zoho uses various cookies in order to create the User's account and to provide the platform for providing the support services.</td>
                      <td className="px-4 py-2 border-b">Session</td>
                      <td className="px-4 py-2 border-b">1st Party</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-2 border-b">consent-policy</td>
                      <td className="px-4 py-2 border-b">www.eyecontrol.co.il</td>
                      <td className="px-4 py-2 border-b">Stores the user's choices regarding cookie consent</td>
                      <td className="px-4 py-2 border-b">1 Year</td>
                      <td className="px-4 py-2 border-b">1st Party</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h3 className="text-lg font-medium mt-4">Analytics Cookies</h3>
              <p>Analytics cookies allow us and our analytics providers to recognize and count the number of users on our Websites and/or Platforms, see how users interact with our Websites and/or Platforms. We use this information to improve our Websites, Platforms and services.</p>
              
              <div className="overflow-x-auto my-4">
                <table className="min-w-full border border-border">
                  <thead className="bg-muted">
                    <tr>
                      <th className="px-4 py-2 text-left border-b">Cookie Name</th>
                      <th className="px-4 py-2 text-left border-b">Provider/domain</th>
                      <th className="px-4 py-2 text-left border-b">Purpose</th>
                      <th className="px-4 py-2 text-left border-b">Duration</th>
                      <th className="px-4 py-2 text-left border-b">Type</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="px-4 py-2 border-b">_ga (Google Analytics)</td>
                      <td className="px-4 py-2 border-b">Google</td>
                      <td className="px-4 py-2 border-b">We use Google Analytics to collect information on your use of our Websites and/or Platforms to improve our Websites and/or Platforms.</td>
                      <td className="px-4 py-2 border-b">2 years</td>
                      <td className="px-4 py-2 border-b">1st party</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-2 border-b">mp_</td>
                      <td className="px-4 py-2 border-b">Mixpanel</td>
                      <td className="px-4 py-2 border-b">It stores a unique identifier for a user's session on the Mixpanel platform, allowing Mixpanel to track user activity such as clicks, page views, and other interactions</td>
                      <td className="px-4 py-2 border-b">1 Year</td>
                      <td className="px-4 py-2 border-b">1st Party</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold">How to manage your cookie settings</h2>
              <p>There are various ways in which you can manage and control your cookie settings. Most browsers allow to reject cookies, or indicate when a cookie is being sent. When you opt-out, an opt-out cookie will be placed on your device. The opt-out cookie is browser and device specific and will only last until cookies are cleared from your browser or device. Please note, that if you choose to decline cookies, you may not be able to fully experience the full features our Websites and/or Platforms.</p>

              <h3 className="text-lg font-medium mt-4">Turning off cookies via your web browser</h3>
              <p>Most web browsers will provide you with some general information about cookies, enable you to see what cookies are stored on your device, allow you to delete all or part of them, and enable you to block or allow cookies for all websites or individually selected websites. You can also normally turn off third-party cookies separately. Please note that the settings offered by a browser or device often only apply to that particular browser or device. Information about cookies is usually found in the "Help" section of the web browser. Below are some links to some commonly used web browsers:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li><a href="https://support.google.com/chrome/answer/95647?hl=en" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Chrome</a></li>
                <li><a href="https://support.google.com/chrome/answer/2392971?hl=en" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Chrome for mobile</a></li>
                <li><a href="https://support.microsoft.com/en-us/help/17442/windows-internet-explorer-delete-manage-cookies" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Internet Explorer and Microsoft Edge</a></li>
                <li><a href="https://support.mozilla.org/en-US/kb/cookies-information-websites-store-on-your-computer" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Mozilla Firefox</a></li>
                <li><a href="https://support.apple.com/kb/PH19214?locale=en_US" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Safari</a></li>
              </ul>

              <h3 className="text-lg font-medium mt-4">Turning off third-party cookies</h3>
              <p>You can turn off certain third-party targeting/advertising cookies by visiting the following link: <a href="http://optout.networkadvertising.org/?c=1" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Network Advertising Initiative</a>.</p>
            </section>

            <p className="text-sm text-muted-foreground mt-8">All rights reserved © EyeFree Assisting Communication Ltd.</p>
          </div>
        </ScrollArea>
      </div>
    </MainLayout>
  );
};

export default CookiePolicy;
